import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { Modal, useTags, KINDS } from "../components/ui";
import DripFunnel from "../components/DripFunnel";
import FunnelStats from "../components/FunnelStats";
import SystemFunnels, { FunnelSection } from "../components/SystemFunnels";
import SalesPipeline from "../components/SalesPipeline";
import { WonHistory } from "../components/WonHistory";

const EMPTY = { name: "", channel: "email", audience_kind: "lead", audience_tag_id: "", template_id: "", message: "", status: "bozza" };

const STATUS_LABEL = { bozza: "Bozza", attivo: "Attivo", in_pausa: "In pausa" };

export default function Funnel() {
  const { tags } = useTags();
  const [connectors, setConnectors] = useState([]);
  const [list, setList] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [modal, setModal] = useState(null); // {data, editing}

  const load = useCallback(() => {
    api.get("/funnels").then((r) => setList(r.data)).catch(() => {});
    api.get("/funnels/connectors").then((r) => setConnectors(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/email-templates").then((r) => setTemplates(r.data)).catch(() => {}); }, []);

  const f = (k, v) => setModal((m) => ({ ...m, data: { ...m.data, [k]: v } }));

  const save = async () => {
    const { data, editing } = modal;
    const payload = { ...data };
    if (!payload.audience_tag_id) delete payload.audience_tag_id;
    if (!payload.template_id) delete payload.template_id;
    try {
      if (editing) await api.put(`/funnels/${data.id}`, payload);
      else await api.post("/funnels", payload);
      setModal(null); load();
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  const setStatus = async (fn, status) => {
    await api.post(`/funnels/${fn.id}/status?status=${status}`); load();
  };
  const remove = async (id) => { if (window.confirm("Eliminare il funnel?")) { await api.delete(`/funnels/${id}`); load(); } };

  return (
    <div>
      <h1 className="page-title">Funnel</h1>
      <p className="page-sub">Connettori e automazioni di marketing verso clienti e lead</p>

      <FunnelSection title="📰 Funnel Newsletter — Pianificazione fiscale" subtitle="Serie di lezioni via email per chi conferma il consenso" testid="sf-section-newsletter">
        <DripFunnel />
        <FunnelStats />
      </FunnelSection>

      <SystemFunnels />

      <FunnelSection title="🎯 Pipeline commerciale" subtitle="Lead in acquisizione e storico trattative" testid="sf-section-pipeline">
        <SalesPipeline />
        <WonHistory />
      </FunnelSection>

      <FunnelSection title="🔌 Connettori" subtitle="Gmail e WhatsApp per le automazioni" testid="sf-section-connectors">
        <div className="seg-cards" style={{ marginTop: 4 }}>
          {connectors.map((c) => (
            <div key={c.id} className="seg-card" data-testid={`connector-${c.id}`}>
              <div className="lbl" style={{ fontSize: 16 }}>{c.name}</div>
              <div className="desc">{c.description}</div>
              <div style={{ marginTop: 10 }}>
                <span className={"pill " + (c.connected ? "gestita" : "da_gestire")}>{c.status}</span>
              </div>
              {!c.connected && (
                <div style={{ marginTop: 10 }}>
                  <Link className="btn sm primary" to={`/impostazioni?tab=${c.settings_tab}`} data-testid={`connector-config-${c.id}`}>Configura</Link>
                </div>
              )}
            </div>
          ))}
        </div>
      </FunnelSection>

      <div className="toolbar" style={{ marginTop: 18 }}>
        <span className="muted">{list.length} funnel personalizzati</span>
        <div className="spacer" />
        <button className="btn primary" onClick={() => setModal({ data: { ...EMPTY }, editing: false })} data-testid="new-funnel-btn">+ Nuovo funnel</button>
      </div>

      <div className="panel">
        {list.length === 0 ? <p className="empty">Nessun funnel. Creane uno per avviare le automazioni.</p> : (
          <table>
            <thead><tr><th>Nome</th><th>Canale</th><th>Pubblico</th><th>Destinatari</th><th>Stato</th><th></th></tr></thead>
            <tbody>
              {list.map((fn) => (
                <tr key={fn.id} data-testid={`funnel-row-${fn.id}`}>
                  <td>{fn.name}</td>
                  <td>{fn.channel === "whatsapp" ? "WhatsApp" : "Email"}</td>
                  <td>{KINDS.find((k) => k.value === fn.audience_kind)?.label || fn.audience_kind || "Tutti"}{fn.audience_tag_id ? ` · ${tags.find((t) => t.id === fn.audience_tag_id)?.name || "etichetta"}` : ""}</td>
                  <td>{fn.audience_count ?? 0}</td>
                  <td><span className={"pill " + (fn.status === "attivo" ? "gestita" : "da_gestire")}>{STATUS_LABEL[fn.status] || fn.status}</span></td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {fn.status !== "attivo"
                      ? <button className="btn sm primary" onClick={() => setStatus(fn, "attivo")} data-testid={`funnel-activate-${fn.id}`}>Avvia</button>
                      : <button className="btn sm" onClick={() => setStatus(fn, "in_pausa")} data-testid={`funnel-pause-${fn.id}`}>Pausa</button>}{" "}
                    <button className="btn sm" onClick={() => setModal({ data: { ...EMPTY, ...fn }, editing: true })}>Modifica</button>{" "}
                    <button className="btn sm danger" onClick={() => remove(fn.id)}>Elimina</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <Modal title={modal.editing ? "Modifica funnel" : "Nuovo funnel"} onClose={() => setModal(null)}>
          <div className="field"><label>Nome *</label><input value={modal.data.name} onChange={(e) => f("name", e.target.value)} data-testid="funnel-name" placeholder="es. Riattivazione lead 2026" /></div>
          <div className="grid-2">
            <div className="field"><label>Canale</label>
              <select value={modal.data.channel} onChange={(e) => f("channel", e.target.value)} data-testid="funnel-channel">
                <option value="email">Email</option>
                <option value="whatsapp">WhatsApp</option>
              </select>
            </div>
            <div className="field"><label>Tipo di contatto</label>
              <select value={modal.data.audience_kind || ""} onChange={(e) => f("audience_kind", e.target.value)} data-testid="funnel-audience-kind">
                <option value="">Tutti</option>
                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </div>
          </div>
          <div className="field"><label>Etichetta (facoltativa)</label>
            <select value={modal.data.audience_tag_id || ""} onChange={(e) => f("audience_tag_id", e.target.value)}>
              <option value="">— tutte —</option>
              {tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          {modal.data.channel === "email" ? (
            <div className="field"><label>Template email</label>
              <select value={modal.data.template_id || ""} onChange={(e) => f("template_id", e.target.value)}>
                <option value="">— nessuno —</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          ) : (
            <div className="field"><label>Messaggio WhatsApp</label><textarea rows={3} value={modal.data.message || ""} onChange={(e) => f("message", e.target.value)} placeholder="Ciao {{nome}}, …" /></div>
          )}
          <p className="muted" style={{ fontSize: 12 }}>Le automazioni di invio si attivano quando il connettore relativo è collegato.</p>
          <div className="modal-actions">
            <button className="btn" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn primary" onClick={save} disabled={!modal.data.name} data-testid="funnel-save">Salva</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
